<template>
	<div class="image-picker">
		<label v-if="label" class="pp-label">{{ label }}</label>
		<div class="image-picker__thumb" :class="{ 'image-picker__thumb--empty': !hasRealImage }">
			<img v-if="hasRealImage" :src="value" alt="">
			<span v-else class="image-picker__empty">Segnaposto: carica o scegli un'immagine</span>
		</div>
		<!-- 2x2: in fila i quattro bottoni non stanno nel pannello e "Genera" usciva tagliato -->
		<div class="image-picker__actions">
			<v-btn small outlined :loading="uploading" @click="$refs.file.click()">
				<v-icon small left>mdi-upload</v-icon>Carica
			</v-btn>
			<v-btn small outlined @click="openLibrary">
				<v-icon small left>mdi-folder-image</v-icon>Libreria
			</v-btn>
			<v-btn small outlined :color="showUrl ? 'primary' : ''" @click="showUrl = !showUrl">
				<v-icon small left>mdi-link</v-icon>URL
			</v-btn>
			<v-btn small outlined color="primary" @click="openGenerate">
				<v-icon small left>mdi-auto-fix</v-icon>Genera
			</v-btn>
		</div>
		<input ref="file" type="file" accept="image/*" style="display:none" @change="onFileChosen">
		<v-text-field v-if="showUrl" :value="hasRealImage ? value : ''" placeholder="https://..." dense outlined hide-details class="mt-2" @change="emitUrl"></v-text-field>

		<v-dialog v-model="generateDialog" max-width="560" persistent>
			<v-card>
				<v-card-title class="pa-3">
					<v-icon class="mr-2">mdi-auto-fix</v-icon>
					<span class="body-1">Genera immagine</span>
					<v-spacer></v-spacer>
					<v-btn icon small :disabled="generating" @click="generateDialog = false"><v-icon>mdi-close</v-icon></v-btn>
				</v-card-title>
				<v-divider></v-divider>
				<v-card-text class="pa-3">
					<v-textarea v-model="generatePrompt" label="Descrizione dell'immagine (in inglese)" rows="4" row-height="20" auto-grow dense outlined hide-details :disabled="generating"></v-textarea>
					<p class="caption grey--text mt-2 mb-2">
						Funziona per sfondi, ambienti e still life. Non puo' produrre la foto di un tuo prodotto reale, e le scritte dentro l'immagine escono spesso storpiate: il testo mettilo con i blocchi.
					</p>
					<v-select v-model="generateRatio" :items="ratios" label="Proporzioni" dense outlined hide-details :disabled="generating"></v-select>
					<v-alert v-if="generateError" type="error" dense text class="mt-3 mb-0 caption">{{ generateError }}</v-alert>
					<p v-if="lastCost !== null" class="caption grey--text mt-3 mb-0">
						Ultima immagine: {{ lastCost }} $ &middot; {{ lastSeconds }} s &middot; {{ lastModel }}
					</p>
				</v-card-text>
				<v-divider></v-divider>
				<v-card-actions class="pa-3">
					<span class="caption grey--text">Di solito pochi secondi.</span>
					<v-spacer></v-spacer>
					<v-btn text small :disabled="generating" @click="generateDialog = false">Annulla</v-btn>
					<v-btn color="primary" small :loading="generating" :disabled="generatePrompt.trim().length < 10" @click="runGenerate">Genera</v-btn>
				</v-card-actions>
			</v-card>
		</v-dialog>

		<v-dialog v-model="libraryDialog" max-width="640" scrollable>
			<v-card>
				<v-card-title class="pa-3">
					<v-icon class="mr-2">mdi-folder-image</v-icon>
					<span class="body-1">Immagini del template</span>
					<v-spacer></v-spacer>
					<v-btn icon small @click="libraryDialog = false"><v-icon>mdi-close</v-icon></v-btn>
				</v-card-title>
				<v-divider></v-divider>
				<v-card-text class="pa-3" style="max-height:60vh">
					<div v-if="libraryLoading" class="text-center pa-6">
						<v-progress-circular indeterminate size="24"></v-progress-circular>
					</div>
					<p v-else-if="!library.length" class="body-2 grey--text text-center pa-6 mb-0">
						Nessuna immagine caricata per questo template. Usa "Carica".
					</p>
					<div v-else class="image-picker__grid">
						<div v-for="(url, i) in library" :key="i"
							class="image-picker__cell"
							:class="{ 'image-picker__cell--current': url === value }"
							@click="choose(url)">
							<img :src="url" alt="">
						</div>
					</div>
				</v-card-text>
			</v-card>
		</v-dialog>
	</div>
</template>

<script>
import { generateImage } from './generateImage'
import { imageRatios } from './emailBlocks'

/**
 * Selettore immagine per i blocchi email: upload sugli allegati pubblici del
 * template (stesso flusso del builder GrapesJS), libreria delle immagini gia'
 * caricate, URL esterno o generazione. Emette sempre un URL assoluto usabile nella mail.
 */
export default {
	name: 'image-picker',

	props: {
		value: { type: String, default: '' },
		label: { type: String, default: '' },
		// Prompt scritto dall'assistente nel campo imagePrompt del blocco, se c'e'
		prompt: { type: String, default: '' }
	},

	inject: {
		templateId: { default: null }
	},

	data() {
		return {
			uploading: false,
			showUrl: false,
			libraryDialog: false,
			libraryLoading: false,
			libraryLoaded: false,
			library: [],
			generateDialog: false,
			generating: false,
			generatePrompt: '',
			generateRatio: '3:2',
			generateError: null,
			lastCost: null,
			lastModel: null,
			lastSeconds: null,
			// Stesso elenco che usa l'assistente: sta nel catalogo, non qui
			ratios: imageRatios
		}
	},

	computed: {
		hasRealImage() {
			return !!this.value && this.value.indexOf('data:') !== 0
		}
	},

	methods: {
		publicUrl(path) {
			return this.$store.getters.baseUrl + '/api/publicfiles/' + path
		},

		isImagePath(path) {
			return /\.(jpe?g|png|gif|webp|svg)$/i.test(path || '')
		},

		openGenerate() {
			this.generateError = null
			if (!this.generatePrompt) this.generatePrompt = this.prompt || ''
			this.generateDialog = true
		},

		// Rigenerazione a mano di una singola foto: stesso percorso che usa l'assistente
		// quando se le chiede da solo, cambia solo chi preme il bottone
		runGenerate() {
			var self = this
			this.generating = true
			this.generateError = null

			generateImage(this.axios, this.$store.getters.baseUrl, {
				prompt: this.generatePrompt,
				templateId: this.templateId,
				ratio: this.generateRatio
			}).then(function(result) {
				self.generating = false
				self.lastCost = result.cost != null ? result.cost.toFixed(4) : null
				self.lastModel = result.model || null
				self.lastSeconds = result.duration_ms ? Math.round(result.duration_ms / 100) / 10 : null
				if (!result.url) return
				self.library.unshift(result.url)
				self.$emit('input', result.url)
				self.generateDialog = false
			}).catch(function(error) {
				self.generating = false
				var serverMsg = error && error.response && error.response.data && error.response.data.message
				self.generateError = serverMsg || 'Generazione non riuscita. Riprova.'
			})
		},

		emitUrl(url) {
			url = (url || '').trim()
			if (url) this.$emit('input', url)
		},

		choose(url) {
			this.$emit('input', url)
			this.libraryDialog = false
		},

		onFileChosen(e) {
			var file = e.target.files && e.target.files[0]
			e.target.value = ''
			if (!file) return

			var data = new FormData()
			data.append('file', file, file.name)
			data.append('attachable_type', 'template')
			data.append('attachable_id', this.templateId)
			data.append('public', 1)
			data.append('usage', 'email_image')

			var self = this
			this.uploading = true
			this.axios.post('attachments/', data).then(function(response) {
				self.uploading = false
				var url = self.publicUrl(response.data.data.path)
				self.library.unshift(url)
				self.$emit('input', url)
			}).catch(function() {
				self.uploading = false
				self.$store.commit('setSnackbarError')
			})
		},

		openLibrary() {
			this.libraryDialog = true
			if (this.libraryLoaded) return

			var self = this
			this.libraryLoading = true
			this.axios.post('attachmentslists/', { id: this.templateId, attachable_type: 'template' }).then(function(response) {
				var list = response.data.attachments || []
				self.library = list
					.filter(function(a) { return self.isImagePath(a.path) })
					.map(function(a) { return self.publicUrl(a.path) })
				self.libraryLoaded = true
				self.libraryLoading = false
			}).catch(function() {
				self.libraryLoading = false
				self.$store.commit('setSnackbarError')
			})
		}
	}
}
</script>

<style scoped>
/* Vuetify di default usa 28px: troppo arioso rispetto al resto del builder */
.v-textarea >>> textarea {
	line-height: 20px;
}

.image-picker__thumb {
	height: 90px;
	border: 1px solid #e0e0e0;
	border-radius: 4px;
	display: flex;
	align-items: center;
	justify-content: center;
	overflow: hidden;
	background: #fafafa;
	margin-top: 2px;
}

.image-picker__thumb--empty {
	background-image: repeating-linear-gradient(135deg, #eee 0 6px, #fafafa 6px 12px);
}

.image-picker__empty {
	padding: 2px 8px;
	border-radius: 4px;
	background: rgba(255, 255, 255, 0.9);
	font-size: 12px;
	color: rgba(0, 0, 0, 0.6);
}

.image-picker__actions {
	display: grid;
	grid-template-columns: 1fr 1fr;
	gap: 6px;
	margin-top: 6px;
}

.image-picker__actions .v-btn {
	min-width: 0;
}

.image-picker__thumb img {
	max-width: 100%;
	max-height: 100%;
	object-fit: contain;
}

.image-picker__grid {
	display: grid;
	grid-template-columns: repeat(4, 1fr);
	gap: 8px;
}

.image-picker__cell {
	height: 100px;
	border: 2px solid #e0e0e0;
	border-radius: 4px;
	display: flex;
	align-items: center;
	justify-content: center;
	overflow: hidden;
	cursor: pointer;
	background: #fafafa;
}

.image-picker__cell:hover {
	border-color: #1976d2;
}

.image-picker__cell--current {
	border-color: #1976d2;
	box-shadow: 0 0 0 2px rgba(25, 118, 210, 0.25);
}

.image-picker__cell img {
	max-width: 100%;
	max-height: 100%;
	object-fit: contain;
}
</style>
